import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Check, Mail, MessageCircle, Phone } from 'lucide-react';
import { SEO } from '../components/SEO';
import { MallMap } from '../components/MallMap';

const inputClasses =
  'w-full rounded-xl border border-white/15 bg-primary-950/60 px-4 py-3.5 text-[15px] text-white placeholder-gray-500 transition-colors duration-200 focus:border-azure-400 focus:outline-none focus:ring-2 focus:ring-azure-500/30 disabled:opacity-60';

const topics = ['Printing', 'Branding', 'Company Registration', 'Something else'];

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });
  const [sentName, setSentName] = useState('');
  const [hoveredStoreName, setHoveredStoreName] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
        }),
      });

      if (response.ok) {
        setSubmitStatus({
          type: 'success',
          message: 'Thank you! Your message has been sent successfully.'
        });
        setSentName(formData.name);
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus({
          type: 'error',
          message: 'Sorry, something went wrong. Please try again later.'
        });
      }
    } catch {
      setSubmitStatus({
        type: 'error',
        message: 'Sorry, something went wrong. Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const restart = () => setSubmitStatus({ type: null, message: '' });

  return (
    <>
      <SEO
        title="Contact DKSN Business Solutions"
        description="Get in touch with DKSN Business Solutions at Shop 23B, Mall of Mthatha. Visit us for professional company registration, branding, and printing services."
        path="/contact"
      />
      <div className="min-h-screen bg-primary-950 pt-20 text-white">
        {/* Hero — animated on mount, never hidden above the fold */}
        <section className="relative overflow-hidden border-b border-white/[0.06] bg-gradient-to-b from-primary-900/40 to-transparent py-16 sm:py-24">
          <div className="pointer-events-none absolute right-0 top-0 h-[500px] w-[900px] rounded-full bg-azure-500/[0.13] blur-3xl" />
          <div className="section-container relative">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="max-w-3xl space-y-6"
            >
              <span className="eyebrow">Get in touch</span>
              <h1 className="font-display text-4xl font-bold leading-[1.02] tracking-[-0.03em] sm:text-5xl lg:text-6xl">
                Let&rsquo;s talk about{' '}
                <span className="font-serif font-normal italic tracking-[-0.01em] text-azure-300">
                  your project.
                </span>
              </h1>
              <p className="max-w-lg text-lg leading-relaxed text-gray-300">
                WhatsApp, call, email or walk into Shop 23B — we reply to every enquiry within one
                working day.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Contact Form and Information */}
        <section className="pt-12 pb-20 sm:pt-16 sm:pb-28">
          <div className="section-container">
            <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr] lg:items-start">
              {/* Contact Form */}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-b from-[#101B30] to-[#0B1322] p-6 sm:p-11"
              >
                <AnimatePresence mode="wait">
                  {submitStatus.type === 'success' ? (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.4 }}
                      className="py-10 text-center sm:py-16"
                    >
                      <div className="mx-auto mb-6 flex h-[72px] w-[72px] items-center justify-center rounded-3xl border border-azure-500/35 bg-azure-500/10 shadow-[0_0_32px_rgba(91,141,239,0.4)]">
                        <Check className="h-8 w-8 text-azure-300" />
                      </div>
                      <h2 className="mb-3 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                        Message sent!
                      </h2>
                      <p className="mx-auto mb-8 max-w-sm text-gray-400">
                        Thanks {sentName || 'there'} — we&rsquo;ll get back to you within one
                        working day.
                      </p>
                      <button
                        type="button"
                        onClick={restart}
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-gray-100 px-7 py-3.5 text-[15px] font-semibold text-primary-950 shadow-[0_18px_50px_-16px_rgba(255,255,255,0.5)] transition-colors hover:bg-white"
                      >
                        Send another message
                      </button>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="form"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.4 }}
                    >
                      <h2 className="mb-1.5 font-display text-2xl font-bold tracking-[-0.02em] text-white sm:text-3xl">
                        Send us a message
                      </h2>
                      <p className="mb-8 text-gray-400">
                        Tell us a bit about what you need — no detail too small.
                      </p>
                      <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                          <div>
                            <label htmlFor="name" className="mb-2 block text-sm font-semibold text-gray-300">
                              Full name
                            </label>
                            <input
                              type="text"
                              id="name"
                              name="name"
                              placeholder="Your name"
                              value={formData.name}
                              onChange={handleChange}
                              className={inputClasses}
                              required
                              disabled={isSubmitting}
                            />
                          </div>
                          <div>
                            <label htmlFor="email" className="mb-2 block text-sm font-semibold text-gray-300">
                              Email
                            </label>
                            <input
                              type="email"
                              id="email"
                              name="email"
                              placeholder="you@business.co.za"
                              value={formData.email}
                              onChange={handleChange}
                              className={inputClasses}
                              required
                              disabled={isSubmitting}
                            />
                          </div>
                        </div>

                        <div>
                          <span className="mb-2 block text-sm font-semibold text-gray-300">
                            What do you need?
                          </span>
                          <div className="flex flex-wrap gap-2.5">
                            {topics.map((topic) => {
                              const selected = formData.subject === topic;
                              return (
                                <button
                                  key={topic}
                                  type="button"
                                  onClick={() => setFormData((prev) => ({ ...prev, subject: topic }))}
                                  disabled={isSubmitting}
                                  className={`rounded-full border px-4 py-2.5 text-[13.5px] font-medium transition-colors disabled:opacity-60 ${
                                    selected
                                      ? 'border-azure-500/60 bg-azure-500/10 text-white'
                                      : 'border-white/10 text-gray-400 hover:border-white/20 hover:text-gray-300'
                                  }`}
                                >
                                  {topic}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <div>
                          <label htmlFor="message" className="mb-2 block text-sm font-semibold text-gray-300">
                            Message
                          </label>
                          <textarea
                            id="message"
                            name="message"
                            placeholder="Tell us about your project, timeline and budget…"
                            value={formData.message}
                            onChange={handleChange}
                            rows={5}
                            className={`${inputClasses} resize-y`}
                            required
                            disabled={isSubmitting}
                          ></textarea>
                        </div>

                        {submitStatus.type === 'error' && (
                          <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300">
                            {submitStatus.message}
                          </div>
                        )}

                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className={`group inline-flex w-full items-center justify-center gap-2 rounded-full bg-gray-100 px-7 py-4 text-[15px] font-semibold text-primary-950 shadow-[0_18px_50px_-16px_rgba(255,255,255,0.5)] transition-colors hover:bg-white sm:w-auto ${
                            isSubmitting ? 'cursor-not-allowed opacity-70' : ''
                          }`}
                        >
                          <span>{isSubmitting ? 'Sending…' : 'Send message'}</span>
                          {!isSubmitting && (
                            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                          )}
                        </button>
                      </form>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* Contact Information */}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="grid gap-3.5"
              >
                {/* Visit */}
                <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-7">
                  <p className="eyebrow mb-2.5">Visit</p>
                  <p className="mb-3.5 text-[17px] leading-relaxed text-white">
                    Shop 23B, Mall of Mthatha
                    <br />
                    33 Errol Spring Ave, Umtata Part 1, Mthatha, 5099
                  </p>
                  <a
                    href="https://maps.google.com/?q=Mall+of+Mthatha"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center gap-1.5 text-sm font-semibold text-azure-300 transition-colors hover:text-azure-200"
                  >
                    Get directions
                    <ArrowRight className="h-[15px] w-[15px] transition-transform duration-200 group-hover:translate-x-1" />
                  </a>
                </div>

                {/* Call / Email */}
                <div className="grid grid-cols-2 gap-3.5">
                  <a
                    href="tel:+27679210514"
                    className="block rounded-3xl border border-white/10 bg-white/[0.02] p-6 transition-colors hover:border-azure-500/40"
                  >
                    <Phone className="mb-3 h-[19px] w-[19px] text-azure-400" />
                    <p className="mb-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                      Call
                    </p>
                    <p className="text-[15px] text-white">+27 67 921 0514</p>
                  </a>
                  <a
                    href="mailto:sales@dksnbsolutions.co.za"
                    className="block rounded-3xl border border-white/10 bg-white/[0.02] p-6 transition-colors hover:border-azure-500/40"
                  >
                    <Mail className="mb-3 h-[19px] w-[19px] text-azure-400" />
                    <p className="mb-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                      Email
                    </p>
                    <p className="break-all text-sm text-white">sales@dksnbsolutions.co.za</p>
                  </a>
                </div>

                {/* WhatsApp */}
                <a
                  href="https://wa.me/27679210514"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 rounded-3xl border border-azure-500/25 bg-gradient-to-br from-azure-500/[0.14] to-azure-500/[0.03] p-7 transition-colors hover:border-azure-300/60"
                >
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-azure-500/35 bg-azure-500/15">
                    <MessageCircle className="h-5 w-5 text-azure-300" />
                  </div>
                  <div>
                    <p className="font-display text-base font-bold tracking-tight text-white">
                      Chat on WhatsApp
                    </p>
                    <p className="mt-0.5 text-[13.5px] text-gray-400">
                      Usually replies within the hour
                    </p>
                  </div>
                </a>

                {/* Studio hours */}
                <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-7">
                  <p className="eyebrow mb-4">Studio hours</p>
                  <div className="grid gap-2.5 text-[14.5px] text-gray-300">
                    <div className="flex items-center justify-between">
                      <span>Monday – Friday</span>
                      <span className="font-medium text-white">09:00 – 17:00</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Saturday</span>
                      <span className="font-medium text-white">09:00 – 15:00</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Sunday</span>
                      <span className="text-gray-500">Closed</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Map band — genuinely below the fold */}
        <section className="pb-24 sm:pb-32">
          <div className="section-container">
            <motion.div
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="relative h-[46vh] min-h-[340px] overflow-hidden rounded-3xl border border-white/10"
            >
              <div className="absolute inset-0 bg-primary-950">
                <MallMap onHoverStore={setHoveredStoreName} />
              </div>
            </motion.div>
            <div className="mt-3 flex h-6 items-center justify-center text-sm text-gray-400">
              {hoveredStoreName ?? 'Hover over a store to see its name'}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Contact;
